import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, addDoc, updateDoc, deleteDoc, doc, 
  query, where, runTransaction, Timestamp 
} from 'firebase/firestore';

// Helper: format number with thousand separators (UGX)
const formatMoney = (value) => {
  if (value === '' || value === undefined || value === null) return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US');
};

// Helper: generate unique model number based on product name
const generateUniqueModelNumber = (productName, existingModelNumbers) => {
  if (!productName) return '';
  
  // 1. Create base code: first letter of each word, uppercase, max 4 letters
  const words = productName.trim().split(/\s+/);
  let base = words.map(word => word[0]).join('').toUpperCase();
  if (base.length > 4) base = base.slice(0, 4);
  
  // 2. Find existing numbers with same base (e.g., "BBD-001", "BBD-002")
  const regex = new RegExp(`^${base}-(\\d{3})$`);
  let maxSuffix = 0;
  for (const existing of existingModelNumbers) {
    const match = existing.match(regex);
    if (match) {
      const num = parseInt(match[1], 10);
      if (num > maxSuffix) maxSuffix = num;
    }
  }
  
  // 3. Next number, zero-padded to 3 digits
  const nextNumber = (maxSuffix + 1).toString().padStart(3, '0');
  return `${base}-${nextNumber}`;
};

export const ProductManagement = ({ isModal, onClose, onRefresh }) => {
  const [products, setProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [showBOMModal, setShowBOMModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [existingModelNumbers, setExistingModelNumbers] = useState([]);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, 'products'));
      const productsData = await Promise.all(
        snapshot.docs.map(async (doc) => {
          const product = { id: doc.id, ...doc.data() };
          
          const bomQuery = query(collection(db, 'bom'), where('productId', '==', product.id));
          const bomSnapshot = await getDocs(bomQuery);
          const bomItems = bomSnapshot.docs.map(bomDoc => ({ id: bomDoc.id, ...bomDoc.data() }));
          const totalCost = bomItems.reduce((sum, item) => sum + (item.totalCost || 0), 0);
          
          return { ...product, bomItems, totalCost };
        })
      );
      setProducts(productsData);
      const allModelNumbers = productsData.map(p => p.modelNumber).filter(Boolean);
      setExistingModelNumbers(allModelNumbers);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProduct = async (productData) => {
    try {
      const newProduct = {
        ...productData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        status: 'active'
      };
      await addDoc(collection(db, 'products'), newProduct);
      await fetchProducts();
      setShowProductModal(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleUpdateProduct = async (productId, productData) => {
    try {
      await updateDoc(doc(db, 'products', productId), {
        ...productData,
        updatedAt: Timestamp.now()
      });
      await fetchProducts();
      setEditingProduct(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteDoc(doc(db, 'products', productId));
      await fetchProducts();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const handleAddBOM = async (productId, bomItems) => {
    try {
      await runTransaction(db, async (transaction) => {
        const existingBOM = await getDocs(query(collection(db, 'bom'), where('productId', '==', productId)));
        existingBOM.forEach(doc => {
          transaction.delete(doc.ref);
        });
        
        for (const item of bomItems) {
          const bomRef = doc(collection(db, 'bom'));
          transaction.set(bomRef, {
            ...item,
            bomId: bomRef.id,
            productId: productId,
            totalCost: item.quantityPerUnit * item.unitCost,
            createdAt: Timestamp.now()
          });
        }
      });
      await fetchProducts();
      setShowBOMModal(false);
      setSelectedProduct(null);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error adding BOM:', error);
    }
  };

  const content = (
    <div style={localStyles.container}>
      <div style={localStyles.header}>
        <div>
          <h1 style={localStyles.title}>Product Management</h1>
          <p style={localStyles.subtitle}>Manage products and bill of materials</p>
        </div>
        <button style={localStyles.buttonPrimary} onClick={() => setShowProductModal(true)}>
          + Add New Product
        </button>
      </div>

      {loading ? (
        <div style={localStyles.loadingContainer}>
          <div style={localStyles.spinner}></div>
          <p>Loading products...</p>
        </div>
      ) : (
        <div style={localStyles.productsGrid}>
          {products.map(product => (
            <div key={product.id} style={localStyles.productCard}>
              <div style={localStyles.productCardHeader}>
                <div>
                  <h3 style={localStyles.productName}>{product.name}</h3>
                  <p style={localStyles.productModel}>{product.modelNumber}</p>
                </div>
                <div style={localStyles.productActions}>
                  <button 
                    style={localStyles.iconButton}
                    onClick={() => setEditingProduct(product)}
                    title="Edit Product"
                  >
                    ✏️
                  </button>
                  <button 
                    style={localStyles.iconButton}
                    onClick={() => handleDeleteProduct(product.id)}
                    title="Delete Product"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <p style={localStyles.productDescription}>{product.description}</p>
              
              <div style={localStyles.productPricing}>
                <div style={localStyles.priceItem}>
                  <span style={localStyles.priceLabel}>Selling Price</span>
                  <span style={localStyles.priceValue}>{product.sellingPrice?.toLocaleString()} UGX</span>
                </div>
                <div style={localStyles.priceItem}>
                  <span style={localStyles.priceLabel}>Total Cost</span>
                  <span style={{...localStyles.priceValue, ...localStyles.costColor}}>{product.totalCost?.toLocaleString()} UGX</span>
                </div>
                <div style={localStyles.priceItem}>
                  <span style={localStyles.priceLabel}>Gross Profit</span>
                  <span style={{...localStyles.priceValue, ...localStyles.profitColor}}>
                    {(product.sellingPrice - product.totalCost)?.toLocaleString()} UGX
                  </span>
                </div>
                <div style={localStyles.priceItem}>
                  <span style={localStyles.priceLabel}>Margin</span>
                  <span style={{...localStyles.priceValue, ...localStyles.marginColor}}>
                    {((product.sellingPrice - product.totalCost) / product.sellingPrice * 100)?.toFixed(1)}%
                  </span>
                </div>
              </div>
              
              <div style={localStyles.bomSection}>
                <div style={localStyles.bomHeader}>
                  <h4 style={localStyles.bomTitle}>Bill of Materials</h4>
                  <button 
                    style={localStyles.buttonSecondarySmall}
                    onClick={() => {
                      setSelectedProduct(product);
                      setShowBOMModal(true);
                    }}
                  >
                    {product.bomItems?.length ? 'Edit BOM' : 'Add BOM'}
                  </button>
                </div>
                
                {product.bomItems?.length > 0 ? (
                  <div style={localStyles.bomList}>
                    {product.bomItems.map(item => (
                      <div key={item.id} style={localStyles.bomItem}>
                        <span style={localStyles.bomItemName}>{item.materialName}</span>
                        <span style={localStyles.bomItemDetails}>
                          {item.quantityPerUnit} × {item.unitCost.toLocaleString()} = {item.totalCost.toLocaleString()}
                        </span>
                      </div>
                    ))}
                    <div style={localStyles.bomTotal}>
                      <strong>Total Cost: {product.totalCost.toLocaleString()} UGX</strong>
                    </div>
                  </div>
                ) : (
                  <div style={localStyles.bomEmpty}>
                    <p>No materials added yet</p>
                    <button 
                      style={localStyles.linkButton}
                      onClick={() => {
                        setSelectedProduct(product);
                        setShowBOMModal(true);
                      }}
                    >
                      Add Bill of Materials →
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showProductModal && (
        <ProductModal
          onClose={() => setShowProductModal(false)}
          onSubmit={handleCreateProduct}
          existingModelNumbers={existingModelNumbers}
        />
      )}

      {editingProduct && (
        <ProductModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSubmit={(data) => handleUpdateProduct(editingProduct.id, data)}
          existingModelNumbers={existingModelNumbers.filter(mn => mn !== editingProduct.modelNumber)}
        />
      )}

      {showBOMModal && selectedProduct && (
        <BOMModal
          product={selectedProduct}
          existingBOM={selectedProduct.bomItems || []}
          onClose={() => {
            setShowBOMModal(false);
            setSelectedProduct(null);
          }}
          onSubmit={(bomItems) => handleAddBOM(selectedProduct.id, bomItems)}
        />
      )}
    </div>
  );

  if (isModal) {
    return (
      <div style={modalStyles.overlay} onClick={onClose}>
        <div style={{...modalStyles.container, maxWidth: '900px'}} onClick={(e) => e.stopPropagation()}>
          <div style={modalStyles.header}>
            <h2 style={modalStyles.title}>Product Management</h2>
            <button style={modalStyles.closeButton} onClick={onClose}>×</button>
          </div>
          <div style={{maxHeight: '70vh', overflow: 'auto'}}>
            {content}
          </div>
        </div>
      </div>
    );
  }

  return content;
};

// Product Modal Component with auto-generated model number
const ProductModal = ({ product, onClose, onSubmit, existingModelNumbers = [] }) => {
  const [formData, setFormData] = useState({
    name: product?.name || '',
    modelNumber: product?.modelNumber || '',
    description: product?.description || '',
    sellingPrice: product?.sellingPrice || '',
    sellingPriceDisplay: product?.sellingPrice ? formatMoney(product.sellingPrice) : '',
    status: product?.status || 'active'
  });
  const [loading, setLoading] = useState(false);
  const [isModelNumberManuallyEdited, setIsModelNumberManuallyEdited] = useState(!!product?.modelNumber);

  // Auto-generate model number when product name changes (if not manually edited)
  useEffect(() => {
    if (!isModelNumberManuallyEdited && formData.name) {
      const newModelNumber = generateUniqueModelNumber(formData.name, existingModelNumbers);
      setFormData(prev => ({ ...prev, modelNumber: newModelNumber }));
    }
  }, [formData.name, existingModelNumbers, isModelNumberManuallyEdited]);

  const handleModelNumberChange = (e) => {
    setIsModelNumberManuallyEdited(true);
    setFormData({ ...formData, modelNumber: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.modelNumber.trim()) {
      alert('Model number is required');
      return;
    }
    // Check uniqueness (except when editing the same product)
    if (product?.modelNumber !== formData.modelNumber && existingModelNumbers.includes(formData.modelNumber)) {
      alert(`Model number "${formData.modelNumber}" already exists. Please use a unique model number.`);
      return;
    }
    setLoading(true);
    await onSubmit({
      name: formData.name,
      modelNumber: formData.modelNumber,
      description: formData.description,
      sellingPrice: typeof formData.sellingPrice === 'number' ? formData.sellingPrice : parseFloat(formData.sellingPrice) || 0,
      status: formData.status
    });
    setLoading(false);
    onClose();
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.container} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>{product ? 'Edit Product' : 'Add New Product'}</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={modalStyles.form}>
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Product Name *</label>
            <input
              type="text"
              style={modalStyles.input}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Banny Bed Deluxe"
              required
            />
          </div>
          
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Model Number *</label>
            <input
              type="text"
              style={modalStyles.input}
              value={formData.modelNumber}
              onChange={handleModelNumberChange}
              placeholder="Auto-generated from product name"
              required
            />
            <small style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
              Unique identifier – you can edit this, but must be different from existing models.
            </small>
          </div>
          
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Description</label>
            <textarea
              style={{...modalStyles.input, ...modalStyles.textarea}}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Product description..."
              rows="3"
            />
          </div>
          
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Selling Price (UGX) *</label>
            <input
              type="text"
              style={modalStyles.input}
              value={formData.sellingPriceDisplay}
              onChange={(e) => {
                const raw = e.target.value;
                let cleaned = raw.replace(/[^0-9.]/g, '');
                const parts = cleaned.split('.');
                if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
                const numValue = parseFloat(cleaned);
                const formatted = isNaN(numValue) ? '' : formatMoney(numValue);
                setFormData({
                  ...formData,
                  sellingPriceDisplay: formatted,
                  sellingPrice: isNaN(numValue) ? '' : numValue
                });
              }}
              placeholder="0"
              required
            />
          </div>
          
          <div style={modalStyles.formGroup}>
            <label style={modalStyles.label}>Status</label>
            <select
              style={modalStyles.select}
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            >
              <option value="active">Active</option>
              <option value="discontinued">Discontinued</option>
            </select>
          </div>
          
          <div style={modalStyles.actions}>
            <button type="button" style={modalStyles.buttonSecondary} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" style={modalStyles.buttonPrimary} disabled={loading}>
              {loading ? 'Saving...' : product ? 'Update Product' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// BOM Modal Component
const BOMModal = ({ product, existingBOM, onClose, onSubmit }) => {
  const [bomItems, setBomItems] = useState(
    existingBOM.length > 0 
      ? existingBOM.map((item, idx) => ({ 
          ...item, 
          id: idx,
          unitCostDisplay: formatMoney(item.unitCost)
        }))
      : [{ materialName: '', quantityPerUnit: '', unitCost: '', unitCostDisplay: '', id: Date.now() }]
  );
  const [loading, setLoading] = useState(false);

  const addBOMItem = () => {
    setBomItems([...bomItems, { 
      materialName: '', 
      quantityPerUnit: '', 
      unitCost: '', 
      unitCostDisplay: '', 
      id: Date.now() 
    }]);
  };

  const removeBOMItem = (id) => {
    setBomItems(bomItems.filter(item => item.id !== id));
  };

  const updateBOMItem = (id, field, value) => {
    setBomItems(bomItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const calculateTotalCost = () => {
    return bomItems.reduce((sum, item) => {
      const qty = parseFloat(item.quantityPerUnit) || 0;
      const cost = typeof item.unitCost === 'number' ? item.unitCost : parseFloat(item.unitCost) || 0;
      return sum + (qty * cost);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = bomItems.filter(item => 
      item.materialName.trim() !== '' && item.quantityPerUnit && item.unitCost
    );
    
    if (validItems.length === 0) {
      alert('Please add at least one material');
      return;
    }
    
    setLoading(true);
    await onSubmit(validItems.map(item => ({
      materialName: item.materialName,
      quantityPerUnit: parseFloat(item.quantityPerUnit),
      unitCost: typeof item.unitCost === 'number' ? item.unitCost : parseFloat(item.unitCost)
    })));
    setLoading(false);
    onClose();
  };

  const totalCost = calculateTotalCost();
  const profit = product.sellingPrice - totalCost;
  const margin = (profit / product.sellingPrice * 100).toFixed(1);

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={{...modalStyles.container, maxWidth: '700px'}} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2 style={modalStyles.title}>Bill of Materials - {product.name}</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} style={modalStyles.form}>
          <div style={bomStyles.summary}>
            <div style={bomStyles.summaryItem}>
              <span style={{color:'black'}}>Selling Price:</span>
              <strong style={{color:'black'}}>{product.sellingPrice.toLocaleString()} UGX</strong>
            </div>
            <div style={bomStyles.summaryItem}>
              <span style={{color:'black'}}>Total Cost:</span>
              <strong style={bomStyles.costColor}>{totalCost.toLocaleString()} UGX</strong>
            </div>
            <div style={bomStyles.summaryItem}>
              <span style={{color:'black'}}>Gross Profit:</span>
              <strong style={profit >= 0 ? bomStyles.profitColor : bomStyles.lossColor}>
                {profit.toLocaleString()} UGX
              </strong>
            </div>
            <div style={bomStyles.summaryItem}>
              <span style={{color:'green'}}>Margin:</span>
              <strong style={margin >= 0 ? bomStyles.profitColor : bomStyles.lossColor}>{margin}%</strong>
            </div>
          </div>
          
          <div style={bomStyles.itemsList}>
            <h3 style={bomStyles.itemsTitle}>Materials Required</h3>
            {bomItems.map((item) => (
              <div key={item.id} style={bomStyles.itemRow}>
                <input
                  type="text"
                  style={bomStyles.itemInput}
                  placeholder="Material name"
                  value={item.materialName}
                  onChange={(e) => updateBOMItem(item.id, 'materialName', e.target.value)}
                  required
                />
                <input
                  type="number"
                  style={bomStyles.itemInputSmall}
                  placeholder="Qty"
                  value={item.quantityPerUnit}
                  onChange={(e) => updateBOMItem(item.id, 'quantityPerUnit', e.target.value)}
                  required
                />
                <input
                  type="text"
                  style={bomStyles.itemInputSmall}
                  placeholder="Unit cost"
                  value={item.unitCostDisplay}
                  onChange={(e) => {
                    const raw = e.target.value;
                    let cleaned = raw.replace(/[^0-9.]/g, '');
                    const parts = cleaned.split('.');
                    if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
                    const numValue = parseFloat(cleaned);
                    const formatted = isNaN(numValue) ? '' : formatMoney(numValue);
                    updateBOMItem(item.id, 'unitCost', isNaN(numValue) ? '' : numValue);
                    updateBOMItem(item.id, 'unitCostDisplay', formatted);
                  }}
                  required
                />
                <div style={bomStyles.itemTotal}>
                  {((parseFloat(item.quantityPerUnit) || 0) * (parseFloat(item.unitCost) || 0)).toLocaleString()}
                </div>
                <button
                  type="button"
                  style={bomStyles.removeButton}
                  onClick={() => removeBOMItem(item.id)}
                  disabled={bomItems.length === 1}
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
          
          <button type="button" style={modalStyles.buttonSecondary} onClick={addBOMItem}>
            + Add Material
          </button>
          
          <div style={modalStyles.actions}>
            <button type="button" style={modalStyles.buttonSecondary} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" style={modalStyles.buttonPrimary} disabled={loading}>
              {loading ? 'Saving...' : 'Save Bill of Materials'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== STYLES ====================
const localStyles = {
  container: {
    padding: '24px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '28px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '4px'
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b'
  },
  buttonPrimary: {
    background: '#7f1a2e',
    color: '#ffffff',
    padding: '10px 20px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    color: '#64748b'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e2e8f0',
    borderTopColor: '#7f1a2e',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px'
  },
  productsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '20px'
  },
  productCard: {
    background: '#ffffff',
    borderRadius: '16px',
    border: '1px solid #eef2f6',
    overflow: 'hidden',
    transition: 'all 0.2s ease'
  },
  productCardHeader: {
    padding: '16px 20px',
    background: '#f8fafc',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottom: '1px solid #eef2f6'
  },
  productName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: '4px'
  },
  productModel: {
    fontSize: '11px',
    color: '#64748b',
    fontFamily: 'monospace'
  },
  productActions: {
    display: 'flex',
    gap: '8px'
  },
  iconButton: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '4px',
    color: '#94a3b8',
    transition: 'color 0.2s ease'
  },
  productDescription: {
    padding: '16px 20px',
    fontSize: '13px',
    color: '#475569',
    borderBottom: '1px solid #f1f5f9'
  },
  productPricing: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    padding: '16px 20px',
    background: '#fafcff'
  },
  priceItem: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px'
  },
  priceLabel: {
    color: 'black'
  },
  priceValue: {
    fontWeight: '600',
    color: 'black'
  },
  costColor: {
    color: '#f59e0b'
  },
  profitColor: {
    color: 'black'
  },
  marginColor: {
    color: 'black'
  },
  bomSection: {
    padding: '16px 20px',
    borderTop: '1px solid #f1f5f9'
  },
  bomHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px'
  },
  bomTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#334155'
  },
  buttonSecondarySmall: {
    background: '#f1f5f9',
    color: '#475569',
    padding: '4px 12px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '11px',
    cursor: 'pointer'
  },
  bomList: {
    background: '#f8fafc',
    borderRadius: '10px',
    padding: '8px'
  },
  bomItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px',
    fontSize: '12px',
    borderBottom: '1px solid #eef2f6'
  },
  bomItemName: {
    fontWeight: '500',
    color: '#000000'
  },
  bomItemDetails: {
    color: '#64748b'
  },
  bomTotal: {
    padding: '8px',
    textAlign: 'right',
    fontSize: '12px',
    borderTop: '1px solid #eef2f6',
    marginTop: '4px'
  },
  bomEmpty: {
    textAlign: 'center',
    padding: '20px',
    color: '#94a3b8',
    fontSize: '12px'
  },
  linkButton: {
    background: 'none',
    border: 'none',
    color: '#7f1a2e',
    cursor: 'pointer',
    fontSize: '12px',
    marginTop: '8px',
    textDecoration: 'underline'
  }
};

const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  container: {
    background: '#ffffff',
    borderRadius: '24px',
    width: '90%',
    maxWidth: '520px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #eef2f6'
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#0f172a'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#94a3b8'
  },
  form: {
    padding: '24px'
  },
  formGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#334155',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    outline: 'none',
    transition: 'all 0.2s ease'
  },
  textarea: {
    resize: 'vertical',
    fontFamily: 'inherit'
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '10px',
    fontSize: '14px',
    background: '#ffffff',
    cursor: 'pointer'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px'
  },
  buttonPrimary: {
    flex: 1,
    background: '#7f1a2e',
    color: '#ffffff',
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  buttonSecondary: {
    flex: 1,
    background: '#f1f5f9',
    color: '#334155',
    padding: '12px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  }
};

const bomStyles = {
  summary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '12px',
    padding: '16px',
    background: '#f8fafc',
    borderRadius: '12px',
    marginBottom: '24px'
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  costColor: {
    color: '#f59e0b'
  },
  profitColor: {
    color: '#10b981'
  },
  lossColor: {
    color: '#ef4444'
  },
  itemsList: {
    marginBottom: '20px'
  },
  itemsTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#334155',
    marginBottom: '12px'
  },
  itemRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 0.8fr 0.8fr 0.8fr auto',
    gap: '8px',
    marginBottom: '8px',
    alignItems: 'center'
  },
  itemInput: {
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px'
  },
  itemInputSmall: {
    padding: '8px 10px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '13px',
    textAlign: 'center'
  },
  itemTotal: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#475569',
    textAlign: 'right',
    paddingRight: '8px'
  },
  removeButton: {
    background: 'none',
    border: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    color: '#94a3b8'
  }
};

const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);